import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { loadNamespace } from '../utils/i18n'

/**
 * Hook personalizado para traduções de páginas específicas
 * Carrega automaticamente os namespaces necessários
 * 
 * @param {string} pageName - Nome da página (ex: 'home', 'about', 'features')
 * @param {Array} additionalNamespaces - Namespaces adicionais para carregar
 * @returns {Object} - Objeto com função t e outras utilidades do useTranslation
 */
export const usePageTranslation = (pageName, additionalNamespaces = []) => {
  const namespaces = [
    `pages/${pageName}`,
    'common',
    'components',
    ...additionalNamespaces
  ]

  const translation = useTranslation(namespaces)
  const { i18n } = translation
  const [isLoading, setIsLoading] = useState(true)
  const additionalNamespacesKey = JSON.stringify(additionalNamespaces)

  useEffect(() => {
    let mounted = true

    const loadPageNamespaces = async () => {
      setIsLoading(true)
      const currentLanguage = i18n.language || 'pt-BR'

      try {
        // Carregar namespace da página específica
        await loadNamespace(currentLanguage, `pages/${pageName}`)

        // Carregar namespaces adicionais se especificados
        for (const namespace of additionalNamespaces) {
          await loadNamespace(currentLanguage, namespace)
        }
      } catch (error) {
        console.error('Erro ao carregar traduções:', error)
      } finally {
        if (mounted) {
          setIsLoading(false)
        }
      }
    }

    loadPageNamespaces()

    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageName, additionalNamespacesKey, i18n.language])

  // Função auxiliar para traduzir com namespace específico
  const tPage = (key, options) => translation.t(`pages/${pageName}:${key}`, options)
  const tCommon = (key, options) => translation.t(`common:${key}`, options)
  const tComponents = (key, options) => translation.t(`components:${key}`, options)

  return {
    ...translation,
    tPage,
    tCommon,
    tComponents,
    currentLanguage: i18n.language,
    isLoading
  }
}

/**
 * Hook para componentes que precisam apenas de traduções comuns
 */
export const useCommonTranslation = () => {
  return useTranslation(['common', 'components'])
}

/**
 * Hook para componentes de formulário
 */
export const useFormTranslation = () => {
  const translation = useTranslation(['components', 'common'])
  const { i18n } = translation

  useEffect(() => {
    const loadFormNamespace = async () => {
      const currentLanguage = i18n.language || 'pt-BR'
      await loadNamespace(currentLanguage, 'components/forms')
    }

    loadFormNamespace()
  }, [i18n.language])

  const tForm = (key, options) => translation.t(`components/forms:${key}`, options)
  const tCommon = (key, options) => translation.t(`common:${key}`, options)

  return {
    ...translation,
    tForm,
    tCommon
  }
}

/**
 * Hook para componentes de calculadora
 */
export const useCalculatorTranslation = () => {
  const translation = useTranslation(['components', 'common'])
  const { i18n } = translation

  useEffect(() => {
    const loadCalculatorNamespace = async () => {
      const currentLanguage = i18n.language || 'pt-BR'
      await loadNamespace(currentLanguage, 'components/calculators')
    }

    loadCalculatorNamespace()
  }, [i18n.language])

  const tCalc = (key, options) => translation.t(`components/calculators:${key}`, options)
  const tCommon = (key, options) => translation.t(`common:${key}`, options)

  return {
    ...translation,
    tCalc,
    tCommon
  }
}

export default usePageTranslation
